import React, { useState } from "react";
import { api } from "../../api";

// Sync tool — export the current environment's content as one JSON, and import
// a JSON exported from another environment. Alice uses this to copy content
// from preview to production (or vice-versa) without touching MongoDB directly.
const SyncTool = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [file, setFile] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [mode, setMode] = useState("merge");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const doExport = async () => {
    setExporting(true);
    setError(null);
    try {
      const snap = await api.adminExport();
      const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alice-lisi-content-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const onFile = async (f) => {
    setFile(f);
    setSnapshot(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (!f) return;
    try {
      const text = await f.text();
      const parsed = JSON.parse(text);
      if (!parsed.collections || typeof parsed.collections !== "object") {
        throw new Error("Not a valid Alice Lisi content snapshot (missing 'collections')");
      }
      setSnapshot(parsed);
    } catch (e) {
      setError("Could not read file: " + e.message);
      setFile(null);
    }
  };

  const doPreview = async () => {
    if (!snapshot) return;
    setImporting(true);
    setError(null);
    try {
      const r = await api.adminImport(snapshot, { mode, dryRun: true });
      setPreview(r);
    } catch (e) {
      setError("Preview failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const doImport = async () => {
    if (!snapshot) return;
    if (!confirm(
      mode === "merge"
        ? "Merge this snapshot into the CURRENT environment? Existing items with the same id will be updated. New items will be inserted. This cannot be undone easily."
        : "Insert only NEW items from this snapshot? Existing items will be left untouched."
    )) return;
    setImporting(true);
    setError(null);
    try {
      const r = await api.adminImport(snapshot, { mode, dryRun: false });
      setResult(r);
      setPreview(null);
    } catch (e) {
      setError("Import failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setImporting(false);
    }
  };

  const renderCounts = (obj, label) => {
    if (!obj) return null;
    const items = Object.entries(obj).filter(([, v]) => v > 0);
    if (!items.length) return <span className="text-[color:var(--ink-mute)]">— none —</span>;
    return (
      <ul className="mt-1">
        {items.map(([k, v]) => (
          <li key={k} className="font-serif text-[13px]">
            <span className="counter">{v}</span> {k} {label}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl space-y-12">
      {/* Export section */}
      <section>
        <p className="font-serif text-2xl">Download from this environment</p>
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mt-1 max-w-xl">
          Downloads a JSON snapshot with all projects, commercial, exhibitions, films, shop products,
          home slideshow, artist bio, contact messages and newsletter subscribers. Payment orders
          and uploaded image files are not included.
        </p>
        <div className="mt-4">
          <button
            onClick={doExport}
            disabled={exporting}
            className="btn-sober disabled:opacity-40"
            data-testid="sync-export-button"
          >
            {exporting ? "Preparing…" : "Download snapshot"}
          </button>
        </div>
      </section>

      <div className="border-t border-[color:var(--line)]" />

      {/* Import section */}
      <section>
        <p className="font-serif text-2xl">Upload to this environment</p>
        <p className="font-serif text-[13px] text-[color:var(--ink-mute)] mt-1 max-w-xl">
          Load a snapshot exported from the other environment. Choose <em>Merge</em> to bring items
          across and update matching ones, or <em>Insert only</em> to add missing items without
          touching existing ones.
        </p>

        <label className="block mt-6">
          <span className="font-serif text-[13px] text-[color:var(--ink-mute)]">Snapshot file (.json)</span>
          <input
            type="file"
            accept="application/json,.json"
            onChange={(e) => onFile(e.target.files?.[0] || null)}
            className="block mt-2 font-serif text-[14px]"
            data-testid="sync-file-input"
          />
        </label>

        {snapshot && (
          <div className="mt-4 font-serif text-[13px] text-[color:var(--ink-mute)]">
            Snapshot loaded — exported{" "}
            <span className="counter">{snapshot.exported_at || "unknown"}</span>. Contains{" "}
            <span className="counter">
              {Object.values(snapshot.collections || {}).reduce(
                (a, arr) => a + (Array.isArray(arr) ? arr.length : 0),
                0,
              )}
            </span>{" "}
            items across {Object.keys(snapshot.collections || {}).length} collections.
          </div>
        )}

        <fieldset className="mt-6">
          <legend className="font-serif text-[13px] text-[color:var(--ink-mute)] mb-2">Mode</legend>
          <label className="flex items-start gap-3 mb-3 cursor-pointer">
            <input
              type="radio"
              checked={mode === "merge"}
              onChange={() => setMode("merge")}
              className="mt-1"
              data-testid="sync-mode-merge"
            />
            <span className="font-serif">
              <strong>Merge</strong>
              <span className="block text-[13px] text-[color:var(--ink-mute)]">
                Update existing items and insert new ones. Best when moving content between environments.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              checked={mode === "insert_only"}
              onChange={() => setMode("insert_only")}
              className="mt-1"
              data-testid="sync-mode-insert"
            />
            <span className="font-serif">
              <strong>Insert only</strong>
              <span className="block text-[13px] text-[color:var(--ink-mute)]">
                Only add items that don't already exist. Existing items are left untouched — safest option.
              </span>
            </span>
          </label>
        </fieldset>

        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={doPreview}
            disabled={!snapshot || importing}
            className="font-serif text-[14px] tlink"
            data-testid="sync-preview-button"
          >
            {importing && !result ? "Checking…" : "Preview changes"}
          </button>
          <button
            onClick={doImport}
            disabled={!snapshot || importing}
            className="btn-sober disabled:opacity-40"
            data-testid="sync-import-button"
          >
            {importing ? "Importing…" : "Import now"}
          </button>
        </div>

        {error && (
          <p className="font-serif text-red-600 mt-4 text-[13px]">{error}</p>
        )}

        {preview && (
          <div className="mt-6 border border-[color:var(--line)] p-4 bg-[color:var(--paper-2)]">
            <p className="font-serif italic text-[color:var(--ink-mute)] mb-2">
              Preview (dry-run, nothing was changed):
            </p>
            <p className="font-serif text-[13px] font-medium">Would insert:</p>
            {renderCounts(preview.inserted, "new")}
            <p className="font-serif text-[13px] font-medium mt-3">Would update:</p>
            {renderCounts(preview.updated, "existing")}
            <p className="font-serif text-[13px] font-medium mt-3">Would skip:</p>
            {renderCounts(preview.skipped, "already present")}
            {(preview.settings_updated || []).length > 0 && (
              <p className="font-serif text-[13px] mt-3">
                Settings to update: {preview.settings_updated.join(", ")}
              </p>
            )}
          </div>
        )}

        {result && (
          <div className="mt-6 border border-[color:var(--ink)] p-4">
            <p className="font-serif italic text-green-700 mb-2">Import completed successfully.</p>
            <p className="font-serif text-[13px] font-medium">Inserted:</p>
            {renderCounts(result.inserted, "new")}
            <p className="font-serif text-[13px] font-medium mt-3">Updated:</p>
            {renderCounts(result.updated, "existing")}
            <p className="font-serif text-[13px] font-medium mt-3">Skipped:</p>
            {renderCounts(result.skipped, "already present")}
            {(result.settings_updated || []).length > 0 && (
              <p className="font-serif text-[13px] mt-3">
                Settings updated: {result.settings_updated.join(", ")}
              </p>
            )}
          </div>
        )}
      </section>

      <div className="border-t border-[color:var(--line)]" />

      {/* Usage guide */}
      <section>
        <p className="font-serif text-2xl">How to sync preview → production</p>
        <ol className="mt-4 space-y-3 font-serif text-[14px] list-decimal list-inside">
          <li>
            Log into the <strong>preview</strong> admin, come to this tab, click{" "}
            <em>Download snapshot</em>. A JSON file gets saved on your computer.
          </li>
          <li>
            Log into the <strong>production</strong> admin (
            <code className="font-mono text-[12px]">/admin</code> on your live domain), open this tab.
          </li>
          <li>
            Choose the JSON file, pick <em>Merge</em> or <em>Insert only</em>, click{" "}
            <em>Preview changes</em> to see what will happen without touching anything.
          </li>
          <li>
            If the preview looks right, click <em>Import now</em>. Data flows into the current
            environment. Uploaded image files stay per-environment (they aren't included in the JSON).
          </li>
        </ol>
      </section>
    </div>
  );
};

export default SyncTool;
